import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { PublishedUpdateSchema, type PublishedUpdate } from '@content-platform/shared';
import EditorPlaceholder from '../components/EditorPlaceholder';
import { api, apiErrorMessage } from '../lib/api';

type FormValues = { title: string; description: string; body: string };

/**
 * 발행된 글 수정 — GitHub repo가 단일 진실 공급원이므로 D1을 거치지 않고
 * GitHub 파일을 직접 로드/덮어쓴다. slug(URL)는 잠금, updatedAt은 서버가 갱신.
 */
export default function PublishedEditor() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const postQuery = useQuery({
    queryKey: ['published', slug],
    queryFn: () => (slug ? api.getPublishedPost(slug) : null),
    enabled: !!slug,
  });

  const { register, handleSubmit } = useForm<FormValues>({
    values: postQuery.data
      ? {
          title: postQuery.data.frontmatter.title,
          description: postQuery.data.frontmatter.description,
          body: postQuery.data.body,
        }
      : undefined,
  });

  // 제목/설명은 폼 필드로, 나머지 보조 필드(summary/keyPoints/sources/tags 등)는 이 JSON으로 편집.
  const initialJson = useMemo(
    () => (postQuery.data ? JSON.stringify(postQuery.data.frontmatter, null, 2) : ''),
    [postQuery.data],
  );
  const [frontmatterJson, setFrontmatterJson] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [prevInitialJson, setPrevInitialJson] = useState('');
  if (initialJson && prevInitialJson !== initialJson) {
    setPrevInitialJson(initialJson);
    setFrontmatterJson(initialJson);
  }

  const saveMutation = useMutation({
    mutationFn: (update: PublishedUpdate) => {
      if (!slug) throw new Error('save: slug required');
      return api.updatePublished(slug, update);
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['published'] });
      queryClient.invalidateQueries({ queryKey: ['published', slug] });
      toast.success('수정 완료', {
        description: `${res.slug} — 공개 사이트는 ~1분 후 반영`,
        action: {
          label: '열기',
          onClick: () => window.open(res.url, '_blank', 'noopener,noreferrer'),
        },
        duration: 8000,
      });
      navigate('/published');
    },
    onError: (err) => {
      toast.error('수정 실패', { description: apiErrorMessage(err) });
    },
  });

  if (postQuery.isLoading) {
    return <p className="text-slate-500">불러오는 중…</p>;
  }
  if (postQuery.error || !postQuery.data) {
    return (
      <div>
        <p className="text-red-600">글을 불러오지 못했습니다: {apiErrorMessage(postQuery.error)}</p>
        <Link to="/published" className="mt-2 inline-block text-sm underline">
          ← Published 목록으로
        </Link>
      </div>
    );
  }

  // 폼 값 + frontmatter JSON을 merge해 완전한 frontmatter로 검증. slug는 서버가 URL 기준 잠금.
  function onSubmit(data: FormValues) {
    let frontmatterRaw: unknown;
    try {
      frontmatterRaw = JSON.parse(frontmatterJson);
    } catch (e) {
      setFormError(`JSON parse 실패: ${(e as Error).message}`);
      return;
    }
    const merged = {
      ...(frontmatterRaw as Record<string, unknown>),
      title: data.title,
      description: data.description,
    };
    const parsed = PublishedUpdateSchema.safeParse({ frontmatter: merged, body: data.body });
    if (!parsed.success) {
      setFormError(
        `frontmatter 검증 실패 — ${parsed.error.issues
          .map((i) => `${i.path.join('.')}: ${i.message}`)
          .join('; ')}`,
      );
      return;
    }
    setFormError(null);
    saveMutation.mutate(parsed.data);
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">발행 글 수정</h1>
          <code className="mt-1 inline-block rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">
            {postQuery.data.frontmatter.slug}
          </code>
        </div>
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={saveMutation.isPending}
            className="rounded bg-emerald-600 px-3 py-1.5 text-sm text-white hover:bg-emerald-500 disabled:opacity-50"
          >
            {saveMutation.isPending ? '저장 중…' : '수정 저장'}
          </button>
          <Link
            to="/published"
            className="rounded border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-100"
          >
            취소
          </Link>
        </div>
      </header>

      <Field label="제목">
        <input
          type="text"
          {...register('title')}
          className="block w-full rounded border border-slate-300 bg-white px-3 py-2 focus:border-slate-500 focus:outline-none"
        />
      </Field>

      <Field label="설명 (SEO meta description)">
        <input
          type="text"
          {...register('description')}
          className="block w-full rounded border border-slate-300 bg-white px-3 py-2 focus:border-slate-500 focus:outline-none"
        />
      </Field>

      <Field label="slug (URL 고정 — 변경하려면 발행 취소 후 재발행)">
        <input
          type="text"
          value={postQuery.data.frontmatter.slug}
          readOnly
          className="block w-full cursor-not-allowed rounded border border-slate-200 bg-slate-100 px-3 py-2 font-mono text-sm text-slate-500"
        />
      </Field>

      <Field label="본문 (마크다운)">
        <EditorPlaceholder {...register('body')} />
      </Field>

      <Field label="frontmatter (JSON — summary/keyPoints/sources/tags 등 보조 필드)">
        <textarea
          rows={12}
          value={frontmatterJson}
          onChange={(e) => {
            setFrontmatterJson(e.target.value);
            if (formError) setFormError(null);
          }}
          className="block w-full rounded border border-slate-300 bg-white px-3 py-2 font-mono text-xs focus:border-slate-500 focus:outline-none"
        />
        <p className="mt-1 text-xs text-slate-500">
          위 폼의 제목/설명이 우선. slug는 잠금, updatedAt은 저장 시 자동 갱신됩니다.
        </p>
        {formError && <p className="mt-1 text-xs text-red-600">{formError}</p>}
      </Field>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-slate-700">{label}</span>
      {children}
    </label>
  );
}
